import { useNavigate, useRouterState, useSearch } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import SignupModal from "@/components/signup-modal";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { INTERCOM_TOUR_STEPS } from "@/helpers/constants";
import useAuth from "@/hooks/use-auth";
import { cn } from "@/lib/utils";
import type { ExploreSearchParams } from "@/routes/explore/index";

import BookingFilter from "../components/booking-filter";
import ScaryFilter from "../components/scary-filter";
import ChristmasFilter from "../filters/christmas-filter";
import IncludeFilter from "../filters/include-filter";
import useSearchFilters from "../hooks/use-search-filters";
import useSelectedSortOption from "../hooks/use-selected-sort-option";
import useSortMenuOptions from "../hooks/use-sort-menu-options";
import CategoryFilter from "../list/category-filter";
import { FiltersIcon } from "../mobile-search-header/icons";
import { IncludeEnum, SortEnum } from "../types";
import FiltersModal, { type FiltersModalProps } from "./filters-modal";
import { ClearIcon, DownIcon, SelectedIcon } from "./icons";

function MenuButtonWithClear({
  className,
  ...props
}: React.ComponentProps<"button">) {
  const search = useSearch({ strict: false }) as ExploreSearchParams;
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const handleClear = () => {
    const { include: _, ...newSearch } = search;
    navigate({ search: newSearch, to: pathname });
  };

  return (
    <div className="h-7 rounded-[30px] bg-white pr-1">
      <Button
        className={cn("pl-3 font-medium text-12 text-berry", className)}
        {...props}
      />
      <Button aria-label="Clear" className="h-full w-6" onClick={handleClear}>
        <ClearIcon />
      </Button>
    </div>
  );
}

interface DesktopSearchHeaderProps {
  filterProps: FiltersModalProps;
}

export default function DesktopSearchHeader({
  filterProps,
}: DesktopSearchHeaderProps) {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isSignupOpen, setIsSignupOpen] = useState(false);
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);

  const { isUser } = useAuth();
  const search = useSearch({ strict: false }) as ExploreSearchParams;
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { appliedFiltersCount } = useSearchFilters();
  const [selectSortOption, setSelectSortOption] = useSelectedSortOption();
  const sortMenuOptions = useSortMenuOptions(true);

  const sortOptionConfig = useMemo(
    () => sortMenuOptions[selectSortOption],
    [sortMenuOptions, selectSortOption]
  );

  const selectIncludeFilter = useMemo(() => {
    if (typeof search.include === "string" && search.include in IncludeEnum) {
      return search.include as IncludeEnum;
    }

    return null;
  }, [search.include]);

  const handleClearFilters = () => filterProps.onClear(true);

  const handleIncludeFilter = (filter: IncludeEnum | "") => {
    // if unauthenticated user attempts to use logged in features, open the signup modal to prompt them to sign up
    if (!isUser) {
      setIsSignupOpen(true);
      return;
    }

    navigate({ search: { ...search, include: filter }, to: pathname });
  };

  const handleSortOption = (option: SortEnum) => () => {
    if (!isUser && option === SortEnum.MY_RATING) {
      setIsSignupOpen(true);
    } else {
      setIsSortMenuOpen(false);
      setSelectSortOption(option);
    }
  };

  return (
    <>
      <SignupModal onOpenChange={setIsSignupOpen} open={isSignupOpen} />
      <div className="fixed z-10 hidden w-1/2 bg-[url(/images/body.png)] bg-midnight-express bg-position-[0_370px] bg-size-auto md:block">
        <CategoryFilter />
        <div className="flex flex-col items-stretch gap-3 p-6">
          <div className="flex justify-between">
            <div className="flex gap-2">
              <IncludeFilter
                CustomMenuButton={
                  selectIncludeFilter ? MenuButtonWithClear : undefined
                }
                onChange={handleIncludeFilter}
                value={selectIncludeFilter || IncludeEnum.ALL}
              />
              {appliedFiltersCount ? (
                <div
                  className="relative flex h-7 items-center rounded-[30px] bg-white after:absolute after:-top-1 after:-right-1 after:flex after:size-4 after:items-center after:justify-center after:rounded-full after:border after:border-honeyflower after:bg-barbie-pink after:font-medium after:text-10 after:text-white after:content-[attr(data-count)]"
                  data-count={appliedFiltersCount}
                >
                  <Button
                    className={cn(
                      INTERCOM_TOUR_STEPS.filterButton,
                      "h-full pl-3 font-medium text-12 text-berry"
                    )}
                    onClick={() => setIsFiltersOpen(true)}
                  >
                    Filters
                  </Button>
                  <Button
                    aria-label="Clear"
                    className="pr-3 pl-2"
                    onClick={handleClearFilters}
                  >
                    <ClearIcon />
                  </Button>
                </div>
              ) : (
                <Button
                  className={cn(
                    INTERCOM_TOUR_STEPS.filterButton,
                    "h-7 rounded-[30px] border border-white/50 px-3 font-medium text-12 text-white transition-colors hover:bg-white/10"
                  )}
                  onClick={() => setIsFiltersOpen(true)}
                >
                  Filters
                  <FiltersIcon className="text-white" />
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <p className="font-medium text-12 text-white">Sort by</p>
              <Popover onOpenChange={setIsSortMenuOpen} open={isSortMenuOpen}>
                <PopoverTrigger
                  className={cn(
                    INTERCOM_TOUR_STEPS.sortButton,
                    "relative z-3 flex h-7 items-center rounded-[30px] border border-white/50 pr-2.5 transition-colors hover:bg-white/10 [&>span]:flex [&>span]:items-center",
                    selectSortOption === SortEnum.COMMUNITY_SCORE
                      ? "pl-1.5"
                      : "pl-2",
                    selectSortOption === SortEnum.DISTANCE &&
                      "[&_svg:first-of-type]:h-4.5 [&_svg:first-of-type]:w-3 [&_svg:first-of-type]:text-dark-magenta"
                  )}
                >
                  {sortOptionConfig?.icon}
                  <span className="mx-2 font-medium text-12 text-white">
                    {sortOptionConfig?.label}
                  </span>
                  <DownIcon color="white.100" />
                </PopoverTrigger>
                <PopoverContent
                  align="end"
                  asChild
                  className="items-between flex w-auto min-w-56 flex-col gap-2.5 rounded-[15px] bg-space-gray-dark p-2.5 shadow-0-0-8 shadow-black/25"
                >
                  <ul>
                    {Object.entries(sortMenuOptions).map(
                      ([key, { icon, label }]) => (
                        <Button
                          className="flex h-10 justify-start rounded-[10px] px-2.5 transition-colors hover:bg-white/10"
                          key={key}
                          onClick={handleSortOption(key as SortEnum)}
                        >
                          {icon}
                          <span className="mr-auto text-14 text-white">
                            {label}
                          </span>
                          {key === selectSortOption ? (
                            <SelectedIcon />
                          ) : (
                            <div className="size-5 rounded-full border border-white/30" />
                          )}
                        </Button>
                      )
                    )}
                  </ul>
                </PopoverContent>
              </Popover>
            </div>
            <FiltersModal
              onOpenChange={setIsFiltersOpen}
              open={isFiltersOpen}
              {...filterProps}
            />
          </div>
          <div className="flex items-center gap-2">
            <ChristmasFilter className="w-[132px]" />
            <ScaryFilter />
            <BookingFilter />
          </div>
        </div>
      </div>
    </>
  );
}
